<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { RouterLink } from 'vue-router';
import { db } from '../db/client';
import { ensureScanner, recognisePhoto } from '../recognition/scan';
import type { ItemRow } from '../types';

const fileInput = useTemplateRef<HTMLInputElement>('fileInput');
const imgEl = useTemplateRef<HTMLImageElement>('imgEl');

const imageUrl = ref<string | null>(null);
const cropRel = ref<{ x: number; y: number; size: number }>({ x: 0.15, y: 0.15, size: 0.7 });

interface Candidate { pk: number; score: number; item: ItemRow }
const candidates = ref<Candidate[]>([]);
const busy = ref(false);
const error = ref<string | null>(null);
const ready = ref(false);
const totalHashes = ref(0);

onMounted(async () => {
  try {
    const { count } = await ensureScanner();
    totalHashes.value = count;
    ready.value = true;
  } catch (e) {
    error.value = (e as Error).message;
  }
});

function onPickFile(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (!f) return;
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value);
  imageUrl.value = URL.createObjectURL(f);
  candidates.value = [];
}

const TILE_RENDER = 64;
const SPRITE_COLS = 32;

function spriteStyleFor(item: ItemRow) {
  return {
    backgroundPosition: `-${item.sprite_x * TILE_RENDER}px -${item.sprite_y * TILE_RENDER}px`,
    backgroundSize: `${SPRITE_COLS * TILE_RENDER}px auto`,
    width: `${TILE_RENDER}px`,
    height: `${TILE_RENDER}px`,
  };
}

// Crop overlay в относительных координатах [0..1] — рисуется поверх <img>.
const cropStyle = computed(() => ({
  left: `${cropRel.value.x * 100}%`,
  top: `${cropRel.value.y * 100}%`,
  width: `${cropRel.value.size * 100}%`,
  height: `${cropRel.value.size * 100}%`,
}));

async function recognise() {
  if (!imgEl.value) return;
  busy.value = true;
  error.value = null;
  candidates.value = [];
  try {
    const img = imgEl.value;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const side = Math.min(nw, nh) * cropRel.value.size;
    const sx = cropRel.value.x * Math.min(nw, nh) + (nw - Math.min(nw, nh)) / 2;
    const sy = cropRel.value.y * Math.min(nw, nh) + (nh - Math.min(nw, nh)) / 2;

    const t0 = performance.now();
    const top = await recognisePhoto(img, { sx, sy, side }, 5);
    const t1 = performance.now();
    console.log('[scan] match in', (t1 - t0).toFixed(0), 'ms', top);
    const items = await db().byPks(top.map((m) => m.pk));
    const byPk = new Map(items.map((it) => [it.pk, it]));
    candidates.value = top
      .map((m) => ({ ...m, item: byPk.get(m.pk)! }))
      .filter((c) => c.item);
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

// Простейший drag overlay'я: фиксируем pointerdown, двигаем .x .y в [0..1-size].
let drag = false;
let dragOff = { x: 0, y: 0 };
function onPointerDown(e: PointerEvent) {
  drag = true;
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  dragOff = {
    x: e.clientX - rect.left - cropRel.value.x * rect.width,
    y: e.clientY - rect.top - cropRel.value.y * rect.height,
  };
}
function onPointerMove(e: PointerEvent) {
  if (!drag) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const sz = cropRel.value.size;
  let nx = (e.clientX - rect.left - dragOff.x) / rect.width;
  let ny = (e.clientY - rect.top - dragOff.y) / rect.height;
  nx = Math.max(0, Math.min(1 - sz, nx));
  ny = Math.max(0, Math.min(1 - sz, ny));
  cropRel.value = { ...cropRel.value, x: nx, y: ny };
}
function onPointerUp(e: PointerEvent) {
  drag = false;
  try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
}

function setSize(sz: number) {
  const maxOff = 1 - sz;
  cropRel.value = {
    size: sz,
    x: Math.max(0, Math.min(maxOff, cropRel.value.x)),
    y: Math.max(0, Math.min(maxOff, cropRel.value.y)),
  };
}

function clear() {
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value);
  imageUrl.value = null;
  candidates.value = [];
}
</script>

<template>
  <div class="flex h-full flex-col bg-bg">
    <header class="border-b border-stone-800 bg-bg pt-safe">
      <div class="flex items-center gap-2 px-3 py-2">
        <RouterLink to="/" class="text-stone-400 hover:text-stone-100">← Назад</RouterLink>
        <h1 class="ml-2 text-sm font-semibold uppercase tracking-wide text-stone-300">
          Сканер
        </h1>
        <span v-if="ready" class="text-xs text-stone-500">{{ totalHashes }} предметов</span>
        <span v-else class="text-xs text-amber-500">загрузка…</span>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto p-3">
      <div v-if="error" class="mb-3 rounded-md bg-red-900/40 p-2 text-sm text-red-200">{{ error }}</div>

      <div v-if="!imageUrl" class="flex flex-col items-center gap-3 rounded-lg border border-dashed border-stone-700 p-6">
        <p class="text-center text-sm text-stone-400">
          Сделайте фото иконки предмета или выберите из галереи.<br>
          Затем подгоните рамку под иконку и нажмите «Распознать».
        </p>
        <button
          class="rounded-md bg-stone-200 px-4 py-2 text-sm font-semibold text-stone-900 active:bg-stone-300"
          @click="fileInput?.click()"
        >
          Сфотографировать
        </button>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden"
          @change="onPickFile"
        />
      </div>

      <div v-else class="space-y-4">
        <div
          class="relative mx-auto max-w-md select-none overflow-hidden rounded-lg bg-bg-card"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <img
            ref="imgEl"
            :src="imageUrl"
            class="pointer-events-none block w-full object-contain"
            alt="Снимок"
          />
          <div
            class="pointer-events-none absolute border-2 border-amber-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] aspect-square"
            :style="cropStyle"
          />
        </div>

        <div class="flex items-center justify-center gap-2">
          <span class="text-xs text-stone-500">размер рамки:</span>
          <button v-for="s in [0.4, 0.55, 0.7, 0.9, 1.0]" :key="s"
                  @click="setSize(s)"
                  class="rounded-md bg-bg-card px-2 py-1 text-xs text-stone-300 hover:bg-stone-700">
            {{ Math.round(s * 100) }}%
          </button>
        </div>

        <div class="flex gap-2">
          <button
            class="flex-1 rounded-md bg-stone-200 py-2 text-sm font-semibold text-stone-900 disabled:bg-stone-700 disabled:text-stone-400"
            :disabled="busy || !ready"
            @click="recognise"
          >
            {{ busy ? 'Считаю…' : 'Распознать' }}
          </button>
          <button class="rounded-md bg-bg-card px-3 py-2 text-sm text-stone-300" @click="clear">
            Очистить
          </button>
        </div>

        <section v-if="candidates.length" data-testid="scan-results">
          <h2 class="mb-2 text-xs uppercase tracking-wide text-stone-500">Кандидаты</h2>
          <ul class="space-y-2">
            <li
              v-for="(c, i) in candidates"
              :key="c.pk"
              class="flex items-center gap-3 rounded-md bg-bg-card p-2"
            >
              <span class="text-xs text-stone-500 w-4">{{ i + 1 }}</span>
              <div class="sprite-atlas rounded" :style="spriteStyleFor(c.item)" />
              <RouterLink
                :to="{ name: 'item', params: { slug: c.item.slug } }"
                class="min-w-0 flex-1 truncate text-sm text-stone-100 hover:underline"
                :data-testid="`scan-result-${i}`"
              >
                {{ c.item.name_ru ?? c.item.name_en }}
                <span v-if="c.item.name_ru" class="ml-1 text-xs text-stone-500">{{ c.item.name_en }}</span>
              </RouterLink>
              <span class="text-xs tabular-nums text-stone-400" :data-testid="`scan-distance-${i}`">
                {{ Math.round(c.score * 100) }}%
              </span>
            </li>
          </ul>
          <p class="mt-2 text-xs text-stone-500">
            Уверенность совпадения (больше = ближе). Если предмет не первый — проверьте кандидатов ниже.
          </p>
        </section>
      </div>
    </main>
  </div>
</template>
